/**
 * pulse-sre stores/returns naive UTC datetimes (no `Z` / offset).
 * `new Date("2026-08-05T06:57:19")` is treated as local time in JS — wrong.
 * Normalize those strings to UTC before formatting.
 */

export const IST_TIME_ZONE = 'Asia/Kolkata';

const RELATIVE_OR_PLACEHOLDER = new Set([
  '',
  'unknown',
  'n/a',
  'na',
  'none',
  'now',
  'current',
  't+now',
  't+0',
  'present',
]);

export function normalizeSreTimestamp(
  value: string | Date | null | undefined
): string | Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return value;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const lower = trimmed.toLowerCase();
  if (RELATIVE_OR_PLACEHOLDER.has(lower) || lower.startsWith('t+')) {
    return null;
  }

  // Already has an explicit timezone
  if (
    /[zZ]$/.test(trimmed) ||
    /[+-]\d{2}:\d{2}$/.test(trimmed) ||
    /[+-]\d{4}$/.test(trimmed)
  ) {
    return trimmed;
  }

  // Date-only ISO is already UTC midnight in ES5
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  // Naive datetime → UTC
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(trimmed)) {
    return `${trimmed.replace(' ', 'T')}Z`;
  }

  return trimmed;
}

export function parseSreTimestamp(
  value: string | Date | null | undefined
): Date | null {
  const normalized = normalizeSreTimestamp(value);
  if (normalized == null) return null;
  const date = normalized instanceof Date ? normalized : new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** True when the value parses to a real calendar datetime (not now/unknown/etc.). */
export function hasValidSreTimestamp(
  value: string | Date | null | undefined
): boolean {
  return parseSreTimestamp(value) != null;
}

/**
 * Format an ISO/UTC timestamp for display in the browser's local timezone.
 * Prefer `useDateFormatter().formatDateTime` in Pulse UI so user prefs apply.
 */
export function formatLocalDateTime(
  value: string | Date | null | undefined,
  fallback = ''
): string {
  const date = parseSreTimestamp(value);
  if (!date) {
    if (value == null || value === '') return fallback;
    // Do not echo placeholder labels (now / unknown / T+now)
    if (typeof value === 'string') {
      const lower = value.trim().toLowerCase();
      if (RELATIVE_OR_PLACEHOLDER.has(lower) || lower.startsWith('t+')) {
        return fallback;
      }
      return value;
    }
    return fallback;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).format(date);
}

/**
 * Format a timestamp in India Standard Time for investigation timelines.
 * Returns empty string when the value is missing or not a real datetime.
 */
export function formatIstDateTime(
  value: string | Date | null | undefined
): string {
  const date = parseSreTimestamp(value);
  if (!date) return '';

  const formatted = new Intl.DateTimeFormat('en-IN', {
    timeZone: IST_TIME_ZONE,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).format(date);

  return `${formatted} IST`;
}
